import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { createSubmission } from '../../api/runs';
import { uploadFile } from '../../api/files';
import { ApiError } from '../../api/client';
import ErrorAlert from '../../components/ErrorAlert';
import type { ChallengeFormProps } from './TextChallengeForm';

const MAX_CHARS = 5000;

const CreativeChallengeForm: React.FC<ChallengeFormProps> = ({
  orgSlug,
  runId,
  challenge,
  alreadySubmitted,
  onSubmitted,
  submitAction,
  initialContent,
}) => {
  const { t } = useTranslation('runs');
  const [text, setText] = useState(
    typeof initialContent?.text === 'string' ? initialContent.text : '',
  );
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const captureRef = useRef<HTMLInputElement | null>(null);
  const galleryRef = useRef<HTMLInputElement | null>(null);
  const activeUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (activeUrlRef.current) URL.revokeObjectURL(activeUrlRef.current);
    };
  }, []);

  if (alreadySubmitted) {
    return (
      <Typography variant="body2" sx={{
        color: "text.secondary"
      }}>
        {t('participant.challenge.alreadySubmitted')}
      </Typography>
    );
  }

  const hasText = text.trim().length > 0;
  const hasPhoto = file !== null;
  const canSubmit = (hasText || hasPhoto) && !submitting;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    if (activeUrlRef.current) URL.revokeObjectURL(activeUrlRef.current);
    activeUrlRef.current = selected ? URL.createObjectURL(selected) : null;
    setPreviewUrl(activeUrlRef.current);
    setFile(selected);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasText && !hasPhoto) {
      setError(t('participant.challenge.creative.atLeastOne'));
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const content: Record<string, string> = {};

      if (hasText) {
        content.text = text.trim();
      }

      if (file) {
        setUploadProgress(0);
        const uploaded = await uploadFile(orgSlug, runId, file, setUploadProgress);
        setUploadProgress(100);
        content.photo_url = uploaded.object_key;
      }

      const result = submitAction
        ? await submitAction(content)
        : await createSubmission(orgSlug, runId, {
            run_challenge_id: challenge.id,
            content,
          });
      onSubmitted(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setUploadProgress(null);
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        {error && <ErrorAlert message={error} />}

        <Typography variant="subtitle2">
          {t('participant.challenge.creative.textLabel')}
        </Typography>
        <TextField
          label={t('participant.challenge.creative.textLabel')}
          multiline
          fullWidth
          minRows={3}
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
          placeholder={t('participant.challenge.text.placeholder')}
          helperText={t('participant.challenge.text.charCount', { count: text.length })}
          disabled={submitting}
        />

        <Typography variant="subtitle2">
          {t('participant.challenge.creative.photoLabel')}
        </Typography>

        {previewUrl && (
          <Box
            component="img"
            src={previewUrl}
            alt={t('participant.challenge.photo.preview')}
            sx={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 1 }}
          />
        )}

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={() => captureRef.current?.click()}
            disabled={submitting}
          >
            {t('participant.challenge.photo.capture')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => galleryRef.current?.click()}
            disabled={submitting}
          >
            {t('participant.challenge.photo.gallery')}
          </Button>
        </Stack>

        <input
          ref={captureRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {uploadProgress !== null && (
          <Box>
            <Typography variant="caption">
              {t('participant.challenge.photo.uploading')}
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={!canSubmit}
        >
          {t('participant.challenge.submit')}
        </Button>
      </Stack>
    </Box>
  );
};

export default CreativeChallengeForm;
